/**
 * Slice 49 — offline start / finish.
 * A start or finish tapped with no signal is kept on the phone with its real time and real location,
 * then written to the job unchanged when the phone is back in signal. Nothing here asks the cleaner to do anything.
 */

import { useMemo, useSyncExternalStore } from "react";
import { getPendingTaskFields, useOutboxVersion } from "@/lib/offline/outbox";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_STAMP_SETTINGS,
  normalizeAwayDistanceM,
  takeStamp,
  type JobStamp,
  type PropertyStampSettings,
} from "@/lib/locationStamps";

export type OutboxKind = "start" | "finish_cleaner" | "finish_team";

export interface OutboxEntry {
  id: string;
  kind: OutboxKind;
  taskId: string;
  propertyId: string;
  stamp: JobStamp;
  /** Extra arguments the start needs when replayed (extra dirty answers). */
  extra?: Record<string, unknown>;
  createdAt: string;
}

const OUTBOX_KEY = "stravo:stampOutbox:v1";
const SETTINGS_KEY = (propertyId: string) => `stravo:stampSettings:${propertyId}`;

let entries: OutboxEntry[] = readStorage();
const listeners = new Set<() => void>();
let version = 0;

function readStorage(): OutboxEntry[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as OutboxEntry[]) : [];
  } catch {
    return [];
  }
}

function persist() {
  try {
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(entries));
  } catch {
    /* storage full or private mode: the in-memory copy still holds this session */
  }
  version += 1;
  listeners.forEach((l) => l());
}

export function getOutboxEntries(): OutboxEntry[] {
  return entries;
}

export function enqueueStamp(entry: Omit<OutboxEntry, "id" | "createdAt">): OutboxEntry {
  const full: OutboxEntry = {
    ...entry,
    id: `${entry.kind}:${entry.taskId}:${entry.stamp.at}`,
    createdAt: new Date().toISOString(),
  };
  if (!entries.some((e) => e.id === full.id)) {
    entries = [...entries, full];
    persist();
  }
  return full;
}

export function removeOutboxEntry(id: string) {
  entries = entries.filter((e) => e.id !== id);
  persist();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useStampOutboxVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => 0);
}

/** True when the failure is the network, not the database saying no. */
export function isNetworkFailure(err: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const msg = err instanceof Error ? err.message : typeof err === "object" && err && "message" in err ? String((err as { message: unknown }).message) : "";
  return /failed to fetch|networkerror|network request failed|load failed|fetch failed|offline/i.test(msg);
}

export async function fetchStampSettings(propertyId: string): Promise<PropertyStampSettings> {
  try {
    const { data, error } = await supabase
      .from("properties")
      .select("latitude, longitude, location_stamps_enabled, location_away_distance_m")
      .eq("id", propertyId)
      .maybeSingle();
    if (error || !data) throw error ?? new Error("no property");
    const row = data as unknown as {
      latitude: number | null;
      longitude: number | null;
      location_stamps_enabled: boolean | null;
      location_away_distance_m: number | null;
    };
    const settings: PropertyStampSettings = {
      enabled: row.location_stamps_enabled !== false,
      awayDistanceM: normalizeAwayDistanceM(row.location_away_distance_m),
      lat: row.latitude != null && Number.isFinite(Number(row.latitude)) ? Number(row.latitude) : null,
      lng: row.longitude != null && Number.isFinite(Number(row.longitude)) ? Number(row.longitude) : null,
    };
    try {
      localStorage.setItem(SETTINGS_KEY(propertyId), JSON.stringify(settings));
    } catch {
      /* ignore */
    }
    return settings;
  } catch {
    try {
      const cached = localStorage.getItem(SETTINGS_KEY(propertyId));
      if (cached) return JSON.parse(cached) as PropertyStampSettings;
    } catch {
      /* ignore */
    }
    return DEFAULT_STAMP_SETTINGS;
  }
}

/**
 * Take the stamp, then run the real write. With no signal (or if the network drops mid-write)
 * the stamp is queued and `queuedResult` stands in so the job still moves forward.
 * `replay` is used when the outbox sends a queued stamp: it never re-queues, it just throws on failure.
 */
export async function runWithStamp<T>(args: {
  kind: OutboxKind;
  taskId: string;
  propertyId: string;
  extra?: Record<string, unknown>;
  replay?: { stamp: JobStamp };
  run: (stamp: JobStamp, settings: PropertyStampSettings) => Promise<T>;
  queuedResult: (stamp: JobStamp) => T;
}): Promise<T> {
  const tapAt = new Date().toISOString();
  const settings = await fetchStampSettings(args.propertyId);
  if (args.replay) return args.run(args.replay.stamp, settings);

  const stamp = await takeStamp(settings, tapAt);
  const queue = () => {
    enqueueStamp({ kind: args.kind, taskId: args.taskId, propertyId: args.propertyId, stamp, extra: args.extra });
    return args.queuedResult(stamp);
  };
  if (typeof navigator !== "undefined" && navigator.onLine === false) return queue();
  try {
    return await args.run(stamp, settings);
  } catch (err) {
    if (isNetworkFailure(err)) return queue();
    throw err;
  }
}

type TaskLike = {
  id: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  start_location_lat?: number | null;
  start_location_override_reason?: string | null;
};

/** Show a start or finish that is still waiting to sync as already done. */
export function applyPendingStamps<T extends TaskLike>(task: T | null | undefined, pending: OutboxEntry[]): T | null | undefined {
  if (!task) return task;
  const mine = pending.filter((e) => e.taskId === task.id);
  if (mine.length === 0) return task;
  let next = task;
  const start = mine.find((e) => e.kind === "start");
  if (start && !task.started_at) {
    next = {
      ...next,
      started_at: start.stamp.at,
      status: next.status === "completed" ? next.status : "in_progress",
      start_location_lat: start.stamp.lat,
      start_location_override_reason: start.stamp.reason,
    };
  }
  const finish = mine.find((e) => e.kind !== "start");
  if (finish && !task.completed_at) {
    next = { ...next, completed_at: finish.stamp.at, status: "completed" };
  }
  return next;
}

/** Saved answers (Slice 56) that the job screens should show right away, with or without signal. */
const OVERLAY_FIELDS = [
  "entry_walkthrough_state",
  "checklist_completed",
  "inventory_completed",
  "entry_walkthrough_completed",
  "guest_setup_completed",
  "guest_setup_state",
  "inventory_guest_phase_completed",
  "inventory_supply_phase_completed",
  "closeout_keys_confirmed",
  "closeout_keys_photo_url",
  "closeout_notes",
  "closeout_state",
  "unit_empty_on_arrival",
  "extra_dirty",
  "extra_dirty_notes",
] as const;

export function useWithPendingStamps<T extends TaskLike>(task: T | null | undefined): T | null | undefined {
  const v = useStampOutboxVersion();
  const v2 = useOutboxVersion();
  return useMemo(() => {
    const stamped = applyPendingStamps(task, getOutboxEntries());
    if (!stamped) return stamped;
    const fields = getPendingTaskFields(stamped.id);
    if (!fields) return stamped;
    const merged: Record<string, unknown> = {};
    for (const key of OVERLAY_FIELDS) if (key in fields) merged[key] = fields[key];
    return Object.keys(merged).length ? ({ ...stamped, ...merged } as T) : stamped;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, v, v2]);
}

/** Send queued stamps, oldest first. Stops quietly when the network is still down. */
export async function flushOutbox(send: (entry: OutboxEntry) => Promise<unknown>): Promise<number> {
  let sent = 0;
  const ordered = [...entries].sort((a, b) => a.stamp.at.localeCompare(b.stamp.at));
  for (const entry of ordered) {
    try {
      await send(entry);
      removeOutboxEntry(entry.id);
      sent += 1;
    } catch (err) {
      if (isNetworkFailure(err)) break;
      console.warn("[stampOutbox] could not send, will retry later:", entry.id, err);
    }
  }
  return sent;
}
